import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Pill } from "@/components/StatusPill";
import OnShiftBoard from "@/components/OnShiftBoard";
import { Loader2, Users as UsersIcon, ShieldCheck, ShieldAlert, HelpCircle, SatelliteDish, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageIcon } from "@/components/PageIcon";
import { AddAssetDialog } from "@/components/AddAssetDialog";

export default function PlantTransportPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [crews, setCrews] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [rSettings, setRSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [fleetMatch, setFleetMatch] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/vehicles").then((r) => setItems(r.data.items));
  useEffect(() => {
    load();
    api.get("/crews").then((r) => { setCrews(r.data.crews || []); setCanManage(r.data.can_manage); }).catch(() => {});
    api.get("/maintenance/reminder-settings").then((r) => setRSettings(r.data)).catch(() => {});
    api.get("/quartix/fleet-match").then((r) => setFleetMatch(r.data)).catch(() => {});
  }, []);

  const matchEnabled = !!fleetMatch?.enabled;
  const matchByVehicle = React.useMemo(() => {
    const m = {};
    (fleetMatch?.app_vehicles || []).forEach((v) => { m[v.legacy_id] = v; });
    return m;
  }, [fleetMatch]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put("/maintenance/reminder-settings", { lead_days: Number(rSettings.lead_days), cc: rSettings.cc || null });
      setRSettings({ lead_days: r.data.lead_days, cc: r.data.cc });
      toast.success("Reminder settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSavingSettings(false); }
  };

  const applyCrew = async (vid, crewId) => {
    if (!crewId) return;
    try {
      const r = await api.post(`/vehicles/${vid}/apply-crew`, { crew_id: crewId });
      toast.success(`Assigned crew "${r.data.crew_title}"`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not assign crew"); }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-plant-transport">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <PageIcon />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Plant Transport</h1>
          <p className="text-sm text-slate-500">{items ? items.length : "…"} vehicles, trailers & plant</p>
        </div>
        {canManage && <Button onClick={() => setAdding(true)} className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid="add-asset-btn"><Plus className="w-4 h-4" /> Add plant / transport</Button>}
      </div>
      <AddAssetDialog open={adding} onClose={() => setAdding(false)} onSaved={load} />

      <OnShiftBoard />

      {matchEnabled && (
        <div className="mb-4 flex flex-wrap items-center gap-3 bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3" data-testid="fleet-match-summary">
          <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><SatelliteDish className="w-4 h-4" /></span>
          <div className="text-sm text-slate-700">
            <span className="font-semibold">Quartix fleet match</span>
            <span className="text-slate-400"> · </span>
            <span className="text-emerald-600 font-medium">{fleetMatch.counts.matched} matched</span>
            <span className="text-slate-400"> · </span>
            <span className={fleetMatch.counts.unmatched ? "text-amber-600 font-medium" : "text-slate-500"}>{fleetMatch.counts.unmatched} unmatched</span>
            {fleetMatch.counts.quartix_only > 0 && <>
              <span className="text-slate-400"> · </span>
              <span className="text-rose-600 font-medium">{fleetMatch.counts.quartix_only} in Quartix only</span>
            </>}
          </div>
          {fleetMatch.quartix_only?.length > 0 && (
            <div className="w-full text-xs text-slate-500 mt-1" data-testid="fleet-quartix-only">
              Tracked in Quartix but not registered in the app: {fleetMatch.quartix_only.map((q) => q.registration || q.description || q.vehicleId).filter(Boolean).join(", ")}
            </div>
          )}
        </div>
      )}

      {canManage && rSettings && (
        <div className="mb-4 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-end gap-4" data-testid="reminder-settings">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">MOT / Service reminder window</div>
            <select value={rSettings.lead_days} onChange={(e) => setRSettings((s) => ({ ...s, lead_days: e.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="reminder-lead-select">
              <option value={7}>7 days before</option>
              <option value={14}>14 days before</option>
              <option value={30}>30 days before</option>
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">CC a second recipient (optional)</div>
            <input type="email" value={rSettings.cc || ""} onChange={(e) => setRSettings((s) => ({ ...s, cc: e.target.value }))} placeholder="name@thehottubsuperstore.co.uk" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="reminder-cc-input" />
          </div>
          <button onClick={saveSettings} disabled={savingSettings} className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="reminder-save-btn">{savingSettings ? "Saving…" : "Save"}</button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="vehicles-table">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {["Vehicle", "Registration", ...(matchEnabled ? ["Fleet Match"] : []), "Description", "Mileage", "Purchased", "Depot", "Department", "Assigned Staff", "Assign Crew", "MOT", "Service", "Availability", "Upcoming"].map((h) => (
                  <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!items && <tr><td colSpan={matchEnabled ? 14 : 13} className="px-4 py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
              {items && items.map((v) => (
                <tr key={v.legacy_id} onClick={() => navigate(`/plant-transport/${v.legacy_id}`)} className="border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer transition-colors" data-testid={`vehicle-row-${v.legacy_id}`}>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900 whitespace-nowrap">{v.title}</td>
                  <td className="px-4 py-3 text-xs mono">{v.registration || "—"}</td>
                  {matchEnabled && <td className="px-4 py-3"><MatchBadge m={matchByVehicle[v.legacy_id]} testid={`fleet-match-${v.legacy_id}`} /></td>}
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-[240px] truncate" title={v.description}>{v.description || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{v.odometer || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{v.purchased_date || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{v.depot || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{v.department || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{v.assigned_staff || "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {canManage ? (
                      crews.length === 0
                        ? <span className="text-xs text-slate-400">No crews</span>
                        : (
                          <div className="relative">
                            <select defaultValue="" onChange={(e) => { applyCrew(v.legacy_id, e.target.value); e.target.value = ""; }}
                              className="appearance-none rounded-lg border border-slate-200 bg-white pl-2.5 pr-7 py-1.5 text-xs text-slate-700 hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100 cursor-pointer"
                              data-testid={`vehicle-apply-crew-${v.legacy_id}`}>
                              <option value="">Assign crew…</option>
                              {crews.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                            </select>
                            <UsersIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          </div>
                        )
                    ) : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3"><MiniStat st={v.mot_status} /></td>
                  <td className="px-4 py-3"><MiniStat st={v.service_status} /></td>
                  <td className="px-4 py-3"><Pill colour={v.availability === "Available" ? "green" : "red"}>{v.availability}</Pill></td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{v.upcoming_tasks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function MatchBadge({ m, testid }) {
  if (!m) return <span className="text-xs text-slate-300" data-testid={testid}>—</span>;
  if (m.matched) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-emerald-100 text-emerald-700"
        title={`Matched Quartix: ${m.quartix_registration}${m.quartix_description ? ` (${m.quartix_description})` : ""}`} data-testid={testid}>
        <ShieldCheck className="w-3.5 h-3.5" /> Matched
      </span>
    );
  }
  if (m.no_registration) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-500"
        title="This vehicle has no registration recorded in the app, so it cannot be matched to Quartix." data-testid={testid}>
        <HelpCircle className="w-3.5 h-3.5" /> No reg
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-amber-100 text-amber-700"
      title={`App registration “${m.app_registration}” was not found in the live Quartix fleet. Check the registration or vehicle mapping — nothing has been changed automatically.`} data-testid={testid}>
      <ShieldAlert className="w-3.5 h-3.5" /> No Quartix match
    </span>
  );
}

function MiniStat({ st }) {
  if (!st || st.status === "unset") return <span className="text-xs text-slate-300">—</span>;
  const map = { overdue: "bg-rose-100 text-rose-700", due_soon: "bg-amber-100 text-amber-700", ok: "bg-emerald-100 text-emerald-700" };
  const label = st.status === "overdue" ? "Overdue" : st.status === "due_soon" ? "Due soon" : "OK";
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${map[st.status]}`} title={st.due || ""}>{label}</span>;
}
